import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import useIndustry from "../../../../../../hooks/use-industry";
import {deepEqual, numComparator} from "../../../../../../../core/services/utils/utils";
import Api from "../../../../../../../core/services/api/api";
import {UserProfilePriceLevelsDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Box, CircularProgress, Paper, Typography} from "@mui/material";
import {Edit} from "@mui/icons-material";
import classnames from "classnames";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import {OutlinedDropdown, OutlinedDropdownForm} from "../../../../../../components/base/dropdown/outlined_dropdown";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {newPriceLevelCategoryIdentifier, newPriceLevelIdentifier, priceLevelIdentifier} from "./index";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import Grid2 from "@mui/material/Unstable_Grid2";

const PriceLevelsSection = ({
                                required,
                                values,
                                initialValues,
                                form,
                                submitting,
                                priceLevels: priceLevelsProps,
                                customer,
                                setCustomer,
                                refetchAccounting,
                                deleting,
                            }) => {
    const [fetchingIndustriesForCategories, setFetchingIndustriesForCategories] = useState(false);
    const isMounted = useIsMounted();

    const {industries, cachedIndustry, selectIndustry, categoriesOfCachedIndustry, hasNoIndustries} = useIndustry();
    const categories = useMemo(() => categoriesOfCachedIndustry, [categoriesOfCachedIndustry]);

    const dataGridSaveConfig = useMemo(() => ({
        name: 'CustomerProfilePriceLevelsDataGrid',
        version: 2,
    }), [])

    const priceLevelsList = useMemo(() => {
        const res = [...priceLevelsProps ?? []];
        res?.sort((a, b) => numComparator(a.levelIndex, b.levelIndex));
        return res
    }, [priceLevelsProps])
    const priceLevelOptions = useMemo(() => {
        return priceLevelsList.map(e => ({
                value: e,
                label: e.title,
                id: e.id
            }))
            ?? []
    }, [priceLevelsList])

    const existingCategoryIds = useMemo(() => customer?.priceLevels?.filter(e => !!e.partCategoryId)?.map(e => e.partCategoryId) ?? [], [customer?.priceLevels]);

    const categoryOptions = useMemo(() => {
        return categories
            ?.filter(e => !e.parentId)
            ?.filter(e => !existingCategoryIds.includes(e.id))
            ?.map(e => ({
                value: e,
                label: e.title,
                id: e.id
            })) ?? []
    }, [categories, existingCategoryIds])

    const dataGridData = useMemo(() => customer?.priceLevels?.filter(e => !!e.partCategoryId) ?? [], [customer?.priceLevels])


    const cachedIndustryCategories = useRef({});
    const [dummy, setDummy] = useState(false);

    /**
     * Fetches the list of industries for the existing categoryIds from the server.
     * @type {(function(): Promise<void>)}
     */
    const getIndustriesOfCategories = useCallback(async () => {
        if (!existingCategoryIds?.length || !industries.length)
            return;
        let dataCategoryIdsFromApi = [];
        for (const dataCategoryId of existingCategoryIds) {
            if (!cachedIndustryCategories.current[dataCategoryId]) {
                dataCategoryIdsFromApi.push(dataCategoryId);
            }
        }
        if (dataCategoryIdsFromApi.length) {
            setFetchingIndustriesForCategories(true);
            const response = await Api.getCategoryIndustryIds({
                ids: dataCategoryIdsFromApi,
            });
            if (!isMounted())
                return;
            setFetchingIndustriesForCategories(false);
            if (response?.resultFlag) {
                cachedIndustryCategories.current = {
                    ...cachedIndustryCategories.current,
                    ...response.data.reduce((acc, e) => {
                        acc[e.id] = industries.find(i => i.id === e.industryId);
                        return acc;
                    }, {})
                }
                setDummy(prevState => !prevState);
            }
        }
    }, [existingCategoryIds, isMounted, industries])

    /**
     * With each change in [dataCategoryIds]:
     * - Fetches the list of industries from the server.
     */
    useEffect(() => {
        if (fetchingIndustriesForCategories)
            return;
        getIndustriesOfCategories().then();
    }, [getIndustriesOfCategories, fetchingIndustriesForCategories]);


    /**
     * Adds a new price level to the user price levels by calling the api from the server. If the result of the api
     * is successful, then adds the price level to the list.
     * @type {(function(*=, *=): void)}
     */
    const addPriceLevel = useCallback(async (values, setLoading) => {
        const newPriceLevel = values[newPriceLevelIdentifier];
        const newCategory = values[newPriceLevelCategoryIdentifier];
        setLoading(true);
        const forApi = {
            id: customer.id,
            priceLevels: [
                ...customer.priceLevels.map(e => ({
                    id: e.id,
                    priceLevel: {
                        id: e.priceLevelId,
                        title: e.priceLevelTitle,
                    },
                    partCategory: e.partCategoryId ? {
                        id: e.partCategoryId,
                        title: e.partCategoryTitle,
                    } : null,
                })),
                {
                    priceLevel: newPriceLevel,
                    partCategory: newCategory
                }
            ],
        }
        const response = await Api.updateCustomerPriceLevel(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            void refetchAccounting();
            setCustomer(prevState => ({
                ...prevState,
                priceLevels: response.data.map(e => ({
                    id: e.id,
                    priceLevelId: e.priceLevel.id,
                    priceLevelTitle: e.priceLevel.title,
                    partCategoryId: e.partCategory?.id,
                    partCategoryTitle: e.partCategory?.title,
                })),
            }));
        }
        setLoading(false);
    }, [customer?.id, customer?.priceLevels, isMounted, setCustomer, refetchAccounting])

    /**
     * Updates the price level of the company by calling the api, and if successful will update the state
     * @type {(function(*, *, *=): void)}
     */
    const updatePriceLevels = useCallback(async (priceLevelEntry, newValue, setLoading = null) => {
        if (setLoading)
            setLoading(true);
        const forApi = {
            id: customer.id,
            priceLevels: customer.priceLevels?.length
                ? customer.priceLevels?.map(e =>
                    e.id === priceLevelEntry.id
                        ? {
                            id: e.id,
                            partCategory: e.partCategoryId ? {id: e.partCategoryId, title: e.partCategoryTitle} : e.partCategory,
                            priceLevel: newValue
                        }
                        : {
                            id: e.id,
                            partCategory: e.partCategoryId ? {id: e.partCategoryId, title: e.partCategoryTitle,} : null,
                            priceLevel: {id: e.priceLevelId, title: e.priceLevelTitle}
                        }
                )
                : [
                    {
                        id: priceLevelEntry?.id,
                        partCategory: priceLevelEntry?.partCategoryId ? {id: priceLevelEntry.partCategoryId, title: priceLevelEntry.partCategoryTitle} : null,
                        priceLevel: newValue,
                    }
                ],
        }
        const response = await Api.updateCustomerPriceLevel(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            void refetchAccounting();
            setCustomer(prevState => ({
                ...prevState,
                priceLevels: response.data.map(e => ({
                    id: e.id,
                    priceLevelId: e.priceLevel.id,
                    priceLevelTitle: e.priceLevel.title,
                    partCategoryId: e.partCategory?.id,
                    partCategoryTitle: e.partCategory?.title,
                }))
            }));
        }
        if (setLoading)
            setLoading(false);
        return response?.resultFlag ?? false;
    }, [customer?.id, customer?.priceLevels, isMounted, setCustomer, refetchAccounting])

    /**
     * Removes a price level from the list of price levels for the company by calling the api, and if successful
     * will remove it from the ui.
     * @type {(function(*): void)}
     */
    const removePriceLevel = useCallback(async (priceLevelEntry) => {
        const forApi = {
            id: customer.id,
            priceLevels: customer.priceLevels
                    ?.filter(e => e.id !== priceLevelEntry.id)
                    ?.map(e => ({
                        id: e.id,
                        priceLevel: {id: e.priceLevelId, title: e.priceLevelTitle},
                        partCategory: e.partCategoryId ? {id: e.partCategoryId, title: e.partCategoryTitle} : null,
                    }))
                ?? [],
        }
        const response = await Api.updateCustomerPriceLevel(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            void refetchAccounting();
            setCustomer(prevState => ({
                ...prevState,
                priceLevels: response.data.map(e => ({
                    id: e.id,
                    priceLevelId: e.priceLevel.id,
                    priceLevelTitle: e.priceLevel.title,
                    partCategoryId: e.partCategory?.id,
                    partCategoryTitle: e.partCategory?.title,
                })),
            }));
        }
    }, [customer?.id, customer?.priceLevels, isMounted, setCustomer, refetchAccounting])

    /**
     * Gets users confirmation before removing the current note entirely.
     * @type {(function(*): void)}
     */
    const getRemoveConfirmation = useCallback((priceLevelEntry) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove Price Level for {priceLevelEntry.partCategoryTitle}?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removePriceLevel(priceLevelEntry),
        );
    }, [removePriceLevel])

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     * @@type {(function(*, *, *, *): void)}
     */
    const onCellEdited = useCallback(async (prevValue, newValue, rowKey, colName) => {
        const id = Number(rowKey)
        if (id === -1)
            return;
        switch (colName) {
            case UserProfilePriceLevelsDataGridCellKeys.priceLevel:
                return updatePriceLevels(customer.priceLevels?.find(e => e.id === id), newValue);
            default:
                return;
        }
    }, [customer?.priceLevels, updatePriceLevels])

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: UserProfilePriceLevelsDataGridCellKeys.action,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 0,
            },
            ...(industries.length > 1 ? [
                {
                    title: "Industry",
                    name: UserProfilePriceLevelsDataGridCellKeys.industry,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    resizable: false,
                    width: {
                        type: DataGridColumnWidthTypes.flex,
                        minWidth: 200,
                        size: 1,
                    },
                },
            ] : []),
            {
                title: "Category",
                name: UserProfilePriceLevelsDataGridCellKeys.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    minWidth: 200,
                    size: 1,
                },
            },
            {
                title: "Price Level",
                name: UserProfilePriceLevelsDataGridCellKeys.priceLevel,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    minWidth: 350,
                    size: 1,
                },
            },
        ], [industries.length])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return [...dataGridData, null].map(priceLevelEntry => {
            // eslint-disable-next-line no-unused-vars
            // noinspection JSUnusedLocalSymbols
            const _ = dummy;
            const newRow = priceLevelEntry === null;
            const priceLevel = newRow ? null : initialValues[`${priceLevelIdentifier}${priceLevelEntry.priceLevelId}`];
            const priceLevelWithDictTitle = newRow ? null : priceLevelsList.find(e => e.id === priceLevel?.id);
            const replacementKey = `${cachedIndustryCategories.current[priceLevelEntry?.partCategoryId]?.id}-${priceLevelEntry?.priceLevelId}-${priceLevelEntry?.partCategoryId}`;
            return ({
                key: newRow ? -1 : priceLevelEntry?.id ?? replacementKey,
                data: priceLevelEntry,
                cells: !newRow ? {
                    [UserProfilePriceLevelsDataGridCellKeys.action]:
                        <IconButton
                            color={'paper.error'}
                            onPressed={() => getRemoveConfirmation(priceLevelEntry)}
                        >
                            <RemoveIcon className={'remove-icon'}/>
                        </IconButton>,
                    [UserProfilePriceLevelsDataGridCellKeys.industry]: cachedIndustryCategories.current[priceLevelEntry.partCategoryId]?.title ?? "",
                    [UserProfilePriceLevelsDataGridCellKeys.category]: priceLevelEntry.partCategoryTitle,
                    [UserProfilePriceLevelsDataGridCellKeys.priceLevel]: {
                        value: ({openEditPopover}) =>
                            <>
                                <IconButton
                                    color={'secondary'}
                                    onPressed={() => openEditPopover()}
                                    disabled={submitting}
                                >
                                    {
                                        submitting
                                            ? <CircularProgress size={20} color={'secondary'}/>
                                            : <Edit className={"icon small"}/>
                                    }
                                </IconButton>
                                <p {...dataGridCellTextProps} className={classnames('ml-2')}>
                                    {priceLevelWithDictTitle?.title ?? ''}
                                </p>
                            </>,
                        editable: true,
                        editableValue: values[`${priceLevelIdentifier}${priceLevelEntry.priceLevelId}`] ?? '',
                        EditableContentComponent:
                            <SelectionEditor
                                initialValue={priceLevelWithDictTitle}
                                options={priceLevelOptions}
                            />,
                    },
                } : hasNoIndustries ? {} : {
                    [UserProfilePriceLevelsDataGridCellKeys.action]:
                        <SaveOrRollback
                            column={false}
                            showText={false}
                            show={{
                                save: !!(
                                    !deepEqual({
                                            [newPriceLevelIdentifier]: values[newPriceLevelIdentifier],
                                            [newPriceLevelCategoryIdentifier]: values[newPriceLevelCategoryIdentifier]
                                        },
                                        {
                                            [newPriceLevelIdentifier]: initialValues[newPriceLevelIdentifier],
                                            [newPriceLevelCategoryIdentifier]: initialValues[newPriceLevelCategoryIdentifier]
                                        })
                                    && values[newPriceLevelCategoryIdentifier]
                                    && values[newPriceLevelIdentifier]
                                    && !deleting
                                ),
                            }}
                            onSave={(setLoading) => !submitting && addPriceLevel(values, setLoading)}
                        />,
                    [UserProfilePriceLevelsDataGridCellKeys.industry]:
                        <div className={'w-100 pr-2'}>
                            <OutlinedDropdown
                                label={'Industry'}
                                data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                value={cachedIndustry}
                                onChange={(e) => selectIndustry(e) || form.change(newPriceLevelCategoryIdentifier, null)}
                            />
                        </div>,
                    [UserProfilePriceLevelsDataGridCellKeys.category]:
                        <div className={'w-100 pr-2'}>
                            <OutlinedDropdownForm
                                label={'Category'}
                                name={newPriceLevelCategoryIdentifier}
                                required={required[newPriceLevelCategoryIdentifier]}
                                data={categoryOptions}
                            />
                        </div>,
                    [UserProfilePriceLevelsDataGridCellKeys.priceLevel]:
                        <div className={'w-100 pr-2'}>
                            <OutlinedDropdownForm
                                label={'Price Level'}
                                name={newPriceLevelIdentifier}
                                required={required[newPriceLevelIdentifier]}
                                data={priceLevelOptions}
                            />
                        </div>,
                }
            })
        })
    }, [dataGridData, initialValues, priceLevelsList, values, priceLevelOptions, hasNoIndustries, industries, cachedIndustry, required, categoryOptions, getRemoveConfirmation, submitting, addPriceLevel, selectIndustry, form, dummy, deleting])

    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%', gap: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Price Levels' Settings
                    </Typography>
                </Box>
                <Grid2 container spacing={2}>
                    <Grid2 xs={12}>
                        <OutlinedDropdownForm
                            label={'General Price Level'}
                            name={`priceLevel`}
                            required={required.priceLevel}
                            data={priceLevelOptions}
                        />
                        <SaveOrRollback
                            column={false}
                            show={!deepEqual(values.priceLevel, initialValues.priceLevel) && !deleting}
                            onRollback={() => form.change('priceLevel', initialValues.priceLevel)}
                            onSave={(setLoading) => !submitting && updatePriceLevels(customer.priceLevels?.find(e => !e.partCategoryId), values.priceLevel, setLoading)}
                        />
                    </Grid2>
                    {
                        hasNoIndustries &&
                        <Grid2 xs={12}>
                            <p className={'text-sm text-color-2'}>
                                There are no industries in the system. Please contact the administrator.
                            </p>
                        </Grid2>
                    }
                    <Grid2 xs={12}>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            columns={dataGridColumns}
                            rows={dataGridRows}
                            visibleRows={dataGridRows.length}
                            hideFooter
                            hideToolbar
                            onCellEdited={onCellEdited}
                            classNames={{
                                container: 'px-0',
                                layout: 'pt-0 mt-0'
                            }}
                        />
                    </Grid2>
                </Grid2>
            </Paper>
        </>
    );
}

export default PriceLevelsSection;

const SelectionEditor = ({
                             setValue,
                             value,
                             setCanSubmitChanges,
                             options,
                             initialValue,
                         }) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value is can be submitted.
     */
    useEffect(() => {
        setCanSubmitChanges(value && !deepEqual(initialValue, value));
    }, [value, initialValue, setCanSubmitChanges])

    return (
        <>
            <OutlinedDropdown
                data={options ?? []}
                value={value}
                onChange={(e) => setValue(e)}
            />
        </>
    );
}
